/**
 * §7.6 Healthcare Capacity Overlay. Live projectHospitalCensus call
 * against a chosen pathogen + region, with surge multiplier slider.
 */

import { useMemo, useState } from 'react';
import { findPathogen, listPathogens } from '@biomax/plugin-pathogen-database';
import { findRegion, listRegions } from '@biomax/plugin-region-database';
import { simulateSeird, projectHospitalCensus } from '@biomax/plugin-epidemiology';
import { Sparkline } from '../sparkline';
import { useScenario } from '../ScenarioContext';

export function HealthcareCapacityOverlay() {
  const pathogens = listPathogens();
  const regions = listRegions();

  const { pathogenId, regionId, setPathogenId, setRegionId } = useScenario();
  const [surge, setSurge] = useState<number>(1.5);

  const view = useMemo(() => {
    const pathogen = findPathogen(pathogenId);
    const region = findRegion(regionId);
    if (!pathogen || !region) return null;

    const gamma = 1 / pathogen.infectiousPeriodDays.value;
    const sigma = 1 / pathogen.latentPeriodDays.value;
    const beta = pathogen.R0.value * gamma;
    const ifr = pathogen.ifrByAgeBand[Math.floor(pathogen.ifrByAgeBand.length / 2)]?.ifr ?? 0.005;

    const epi = simulateSeird({
      N: region.population.total, I0: 500,
      beta, gamma, sigma, ifr,
      durationDays: 180, dtDays: 1,
    });

    // Daily new infections = -dS/dt approximated as |S[t-1] - S[t]|.
    const S = epi.compartments.S!;
    const dailyIncidence: number[] = [];
    for (let i = 1; i < S.length; i++) {
      dailyIncidence.push(Math.max(0, S[i - 1]! - S[i]!));
    }

    const ihrFraction = pathogen.icuFraction?.value !== undefined
      ? Math.max(0.02, Math.min(0.20, pathogen.icuFraction.value * 0.5))
      : 0.05;
    const icuFractionOfHosp = pathogen.icuFraction?.value ?? 0.15;

    const hospitalBedsPerCapita = (region.healthcareCapacity?.hospitalBedsPer100k ?? 200) / 100_000;
    const icuBedsPerCapita = (region.healthcareCapacity?.icuBedsPer100k ?? 8) / 100_000;
    const hospitalBedsTotal = hospitalBedsPerCapita * region.population.total;
    const icuBedsTotal = icuBedsPerCapita * region.population.total;

    const losMean = pathogen.hospLengthOfStayDays?.mean ?? 7;
    const losSd = (pathogen.hospLengthOfStayDays?.p95 ?? losMean * 2) / 2;

    const proj = projectHospitalCensus({
      dailyIncidence,
      ihr: ihrFraction,
      losMeanDays: losMean,
      losSdDays: losSd,
      icuFraction: icuFractionOfHosp,
      icuLosMeanDays: losMean + 3,
      icuLosSdDays: losSd,
      hospitalBedsTotal,
      icuBedsTotal,
      surgeMultiplier: surge,
    });

    return {
      pathogen, region, epi, proj,
      hospitalBedsTotal, icuBedsTotal,
    };
  }, [pathogenId, regionId, surge]);

  if (!view) return <div>Loading…</div>;

  const peakHosp = Math.max(...view.proj.hospitalCensus);
  const peakIcu = Math.max(...view.proj.icuCensus);
  const peakHospDay = view.proj.hospitalCensus.indexOf(peakHosp);
  const hospSatDay = view.proj.hospitalSaturationDay;
  const icuSatDay = view.proj.icuSaturationDay;
  const hospUtil = peakHosp / (view.hospitalBedsTotal * surge);
  const icuUtil = peakIcu / (view.icuBedsTotal * surge);

  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">§7.6</span>
        <h1 className="surface__title">Healthcare Capacity Overlay</h1>
      </div>
      <div className="surface__sub">
        Live projectHospitalCensus convolution · gamma-distributed LOS · {view.region.names.english}
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">Scenario</span></div>
        <div className="simctl">
          <label className="simctl__field">
            <span className="simctl__label">Pathogen</span>
            <select value={pathogenId} onChange={e => setPathogenId(e.target.value)}>
              {pathogens.map(p => <option key={p.id} value={p.id}>{p.names.common}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Region</span>
            <select value={regionId} onChange={e => setRegionId(e.target.value)}>
              {regions
                .slice()
                .sort((a, b) => a.names.english.localeCompare(b.names.english))
                .map(r => <option key={r.id} value={r.id}>{r.names.english}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Surge multiplier · ×{surge.toFixed(2)}</span>
            <input type="range" min="1" max="3" step="0.05" value={surge}
              onChange={e => setSurge(parseFloat(e.target.value))} />
          </label>
        </div>
      </div>

      <div className="kpis">
        <div className="kpi">
          <div className="kpi__label">Hospital saturation</div>
          <div className={`kpi__value ${hospSatDay !== null ? 'kpi__value--danger' : 'kpi__value--accent'}`}>
            {hospSatDay !== null ? `Day ${hospSatDay}` : 'NOT REACHED'}
          </div>
          <div className="kpi__sub">{view.hospitalBedsTotal.toFixed(0).toLocaleString()} beds × {surge.toFixed(1)} surge</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">ICU saturation</div>
          <div className={`kpi__value ${icuSatDay !== null ? 'kpi__value--danger' : 'kpi__value--accent'}`}>
            {icuSatDay !== null ? `Day ${icuSatDay}` : 'NOT REACHED'}
          </div>
          <div className="kpi__sub">{view.icuBedsTotal.toFixed(0).toLocaleString()} beds × {surge.toFixed(1)} surge</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Peak hospital census</div>
          <div className="kpi__value kpi__value--signal">{Math.round(peakHosp).toLocaleString()}</div>
          <div className="kpi__sub">day {peakHospDay} · {(hospUtil * 100).toFixed(0)}% of capacity</div>
        </div>
        <div className="kpi">
          <div className="kpi__label">Peak ICU census</div>
          <div className="kpi__value kpi__value--danger">{Math.round(peakIcu).toLocaleString()}</div>
          <div className="kpi__sub">{(icuUtil * 100).toFixed(0)}% of capacity</div>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Hospital census trajectory</span>
          <span className="panel__meta">vs capacity</span>
        </div>
        <Sparkline values={view.proj.hospitalCensus} label="General-ward census" stroke="var(--bm-signal)" />
        <Sparkline values={view.proj.icuCensus} label="ICU census" stroke="var(--bm-danger)" />
        <Sparkline values={view.proj.admissions} label="Daily admissions" stroke="var(--bm-accent)" />
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Bed-utilisation summary</span>
        </div>
        <div className="capbar">
          <span className="capbar__name">General-ward</span>
          <div className="capbar__track">
            <div className={`capbar__fill ${hospUtil > 1 ? 'capbar__fill--danger' : hospUtil > 0.8 ? 'capbar__fill--warn' : ''}`}
              style={{ width: `${Math.min(100, hospUtil * 100)}%` }} />
          </div>
          <span className="capbar__value">{(hospUtil * 100).toFixed(0)}%</span>
        </div>
        <div className="capbar">
          <span className="capbar__name">ICU</span>
          <div className="capbar__track">
            <div className={`capbar__fill ${icuUtil > 1 ? 'capbar__fill--danger' : icuUtil > 0.8 ? 'capbar__fill--warn' : ''}`}
              style={{ width: `${Math.min(100, icuUtil * 100)}%` }} />
          </div>
          <span className="capbar__value">{(icuUtil * 100).toFixed(0)}%</span>
        </div>
      </div>
    </div>
  );
}
